// turf
import { area } from '@turf/turf';

// mapbox lib
import * as CommonSelectors from '@/vue3/mapbox/lib/commonSelectors';
import * as Constants from './constants';
import isEventAtCoordinates from '@/vue3/mapbox/lib/isEventAtCoordinates';
import doubleClickZoom from '@/vue3/mapbox/lib/doubleClickZoom';

// hooks
import { useStore } from '@/store';

const MeasurePolygonMode: any = {};
const store = useStore();

let measurement = 0;
let tempState: any;
let isMeters;
let isMobile;

const createVertex = (parentId, coordinates, path, selected) => {
    return {
        type: 'Feature',
        properties: {
            meta: 'vertex',
            parent: parentId,
            coord_path: path,
            active: selected ? 'true' : 'false',
            measurementTotal: measurement,
            totalMeasurementLabel: `${
                Math.round(
                    measurement * 100 * (isMeters ? 1 : 1.195990046301),
                ) / 100
            } ${isMeters ? 'm²' : 'yd²'}`,
        },
        geometry: {
            type: 'Point',
            coordinates,
        },
    };
};

MeasurePolygonMode.onSetup = function () {
    isMeters = store.state.isMeters;
    isMobile = store.state.isMobile;

    const polygon = this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: {
            active: 'true',
            measurement: measurement,
            measurementLabel: '',
        },
        geometry: {
            type: Constants.geojsonTypes.POLYGON,
            coordinates: [[]],
        },
    });

    this.addFeature(polygon);

    this.clearSelectedFeatures();
    doubleClickZoom.disable(this);
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.activateUIButton(Constants.types.POLYGON);
    this.setActionableState({
        trash: true,
    });

    if (polygon.coordinates.length && polygon.coordinates[0].length < 2) {
        polygon.updateCoordinate('0.0', 0, 0);
    }

    return {
        polygon,
        currentVertexPosition: 0,
    };
};

MeasurePolygonMode.clickAnywhere = function (state, e) {
    const { polygon } = state;

    if (
        state.currentVertexPosition > 0 &&
        isEventAtCoordinates(
            e,
            state.polygon.coordinates[0][state.currentVertexPosition - 1],
        )
    )
        return this.changeMode('MeasurePolygonMode');

    this.updateUIClasses({ mouse: Constants.cursors.ADD });

    const { lng, lat } = e.lngLat;

    polygon.updateCoordinate(`0.${state.currentVertexPosition}`, lng, lat);
    state.currentVertexPosition++;
    polygon.updateCoordinate(`0.${state.currentVertexPosition}`, lng, lat);
};

MeasurePolygonMode.clickOnVertex = function (state) {
    this.changeMode('MeasurePolygonMode');
};

MeasurePolygonMode.onMouseMove = function (
    { polygon, currentVertexPosition },
    e,
) {
    const { lng, lat } = e.lngLat;
    polygon.updateCoordinate(`0.${currentVertexPosition}`, lng, lat);

    if (!CommonSelectors.isVertex(e)) return;

    this.updateUIClasses({ mouse: Constants.cursors.POINTER });
};

MeasurePolygonMode.onTap = function (state, e) {
    tempState = state;
    return this.clickAnywhere(state, e);
};

MeasurePolygonMode.onClick = function (state, e) {
    tempState = state;
    if (CommonSelectors.isVertex(e)) return this.clickOnVertex(state, e);
    return this.clickAnywhere(state, e);
};

MeasurePolygonMode.onKeyUp = function ({ polygon: { id } }, e) {
    const isEnterKey = CommonSelectors.isEnterKey(e);
    const isEscapeKey = CommonSelectors.isEscapeKey(e);

    if (!isEnterKey && !isEscapeKey) return;

    if (isEnterKey) return this.changeMode('MeasurePolygonMode');

    this.deleteFeature([id], { silent: true });
    this.changeMode('MeasurePolygonMode');
};

MeasurePolygonMode.onStop = function ({ polygon, currentVertexPosition }) {
    this.updateUIClasses({ mouse: Constants.cursors.NONE });
    doubleClickZoom.enable(this);
    this.activateUIButton();

    // check to see if we've deleted this feature
    if (this.getFeature(polygon.id) === undefined) return;

    //remove last added coordinate
    polygon.removeCoordinate(`0.${currentVertexPosition}`);

    if (polygon.isValid())
        return this.map.fire(Constants.events.CREATE, {
            features: [polygon.toGeoJSON()],
        });

    this.deleteFeature([polygon.id], { silent: true });
    this.changeMode('MeasurePolygonMode');
};

MeasurePolygonMode.toDisplayFeatures = function (
    { polygon },
    geojson,
    display,
) {
    const {
        properties,
        geometry: { coordinates: coords },
    } = geojson;

    const isActivePolygon = geojson.properties.id === polygon.id;
    geojson.properties.active = isActivePolygon
        ? Constants.activeStates.ACTIVE
        : Constants.activeStates.INACTIVE;

    if (!isActivePolygon) return display(geojson);

    if (geojson.geometry.coordinates.length === 0) return;
    const coordinateCount = geojson.geometry.coordinates[0].length;
    if (coordinateCount < 3) {
        return;
    }
    geojson.properties.meta = Constants.meta.FEATURE;

    measurement = area(geojson);

    polygon.properties.measurement = measurement;
    polygon.properties.measurementTotal = measurement;
    polygon.properties.measurementLabel = `${
        Math.round(measurement * 100 * (isMeters ? 1 : 1.195990046301)) / 100
    } ${isMeters ? 'm²' : 'yd²'}`;

    display(
        createVertex(
            polygon.id,
            geojson.geometry.coordinates[0][0],
            '0.0',
            false,
        ),
    );

    if (coordinateCount > 3 && isMobile) {
        const endPos = geojson.geometry.coordinates[0].length - 3;
        display(
            createVertex(
                polygon.id,
                geojson.geometry.coordinates[0][endPos],
                `0.${endPos}`,
                false,
            ),
        );
    }

    if (coordinateCount <= 4) {
        // If we've only drawn two positions (plus the closer),
        // make a LineString instead of a Polygon
        const lineCoordinates = [
            [
                geojson.geometry.coordinates[0][0][0],
                geojson.geometry.coordinates[0][0][1],
            ],
            [
                geojson.geometry.coordinates[0][1][0],
                geojson.geometry.coordinates[0][1][1],
            ],
        ];
        // create an initial vertex so that we can track the first point on mobile devices
        display({
            type: Constants.geojsonTypes.FEATURE,
            properties: {
                meta: 'initial vertex',
                parent: polygon.id,
            },
            geometry: {
                coordinates: lineCoordinates,
                type: Constants.geojsonTypes.LINE_STRING,
            },
        });

        if (coordinateCount === 3) {
            return;
        }
    }

    // render the Polygon
    return display(geojson);
};

MeasurePolygonMode.onTrash = function (state) {
    this.deleteFeature([state.polygon.id], { silent: true });
};

MeasurePolygonMode.onDrawFinished = function () {
    return tempState;
};

export default MeasurePolygonMode;
